import type {
  MandobCatalogItem, MandobCollectionRow, MandobCustomer, MandobEarnings, MandobOrderRow, MandobProfile,
} from '@erp/shared';
import Decimal from 'decimal.js';
import { Banknote, ClipboardList, Coins, HandCoins, MapPin, PackageCheck, ShoppingBag, Truck, UserPlus, Users, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Badge, Button, Card, Combobox, DataTable, EmptyState, FormField, Input, PhoneInput, Skeleton, StatCard } from '../../components/ui/index.js';
import { MapPicker, type LatLng } from '../../components/ui/MapPicker.js';
import { useAuthStore } from '../../lib/authStore.js';
import {
  createMyCustomer, getMyEarnings, getMyMandob, listMandobCatalog, listMyCollections, listMyCustomers, listMyOrders,
  mandobCollect, mandobDeliver, mandobSell,
} from '../../lib/mandob.js';
import { formatMoney } from '../../lib/money.js';

type Tab = 'overview' | 'customers' | 'sell' | 'collect' | 'activity';

interface SellLine { itemId?: string; itemUnitId?: string; description: string; quantity: string; unitPrice: string }

export function FieldWorkspacePage(): JSX.Element {
  const { t, i18n } = useTranslation('mandob');
  const tenant = useAuthStore((s) => s.tenant);
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';
  const money = (amount: string, currency = baseCurrency): string => formatMoney(amount, { locale: i18n.language, currency });

  const [profile, setProfile] = useState<MandobProfile | null | undefined>(undefined);
  const [earnings, setEarnings] = useState<MandobEarnings | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [orders, setOrders] = useState<MandobOrderRow[]>([]);
  const [collections, setCollections] = useState<MandobCollectionRow[]>([]);
  // Pre-selected customer when jumping into Sell/Collect from the customers list.
  const [preset, setPreset] = useState<{ tab: 'sell' | 'collect'; partyId: string } | null>(null);

  const loadProfile = async (): Promise<void> => {
    try { setProfile(await getMyMandob()); } catch { setProfile(null); }
  };
  const loadEarnings = async (): Promise<void> => {
    try { setEarnings(await getMyEarnings()); } catch { /* ignore */ }
  };
  const loadActivity = async (): Promise<void> => {
    try {
      const [o, c] = await Promise.all([listMyOrders(), listMyCollections()]);
      setOrders(o); setCollections(c);
    } catch { /* ignore */ }
  };
  const refresh = async (): Promise<void> => { await Promise.all([loadEarnings(), loadActivity()]); };

  useEffect(() => {
    void loadProfile(); void loadEarnings(); void loadActivity();
  }, []);

  if (profile === undefined) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }
  if (profile === null) {
    return (
      <EmptyState
        title={t('field.notRep', { defaultValue: 'You are not a field rep' })}
        description={t('field.notRepHint', { defaultValue: 'This workspace is for mandobs. Ask an administrator to set you up.' })}
      />
    );
  }

  const tabs: Array<{ id: Tab; label: string; icon: typeof Users }> = [
    { id: 'overview', label: t('field.overview', { defaultValue: 'Overview' }), icon: Wallet },
    { id: 'customers', label: t('field.customers', { defaultValue: 'My customers' }), icon: Users },
    { id: 'sell', label: t('field.sell', { defaultValue: 'New order' }), icon: ShoppingBag },
    { id: 'collect', label: t('field.collect', { defaultValue: 'Collect' }), icon: HandCoins },
    { id: 'activity', label: t('field.activity', { defaultValue: 'Activity' }), icon: ClipboardList },
  ];

  const goTo = (next: { tab: 'sell' | 'collect'; partyId: string }): void => { setPreset(next); setTab(next.tab); };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('field.title', { defaultValue: 'My route' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('field.subtitle', { defaultValue: 'Sell, collect, and deliver in the field.' })}
            {profile.territory ? ` · ${profile.territory}` : ''}
          </p>
        </div>
        <Badge tone={profile.payScheme === 'salary_commission' ? 'info' : 'neutral'}>
          {t(`scheme.${profile.payScheme}`, { defaultValue: profile.payScheme })}
        </Badge>
      </header>

      <nav className="flex flex-wrap gap-2" aria-label={t('field.title', { defaultValue: 'My route' })}>
        {tabs.map((x) => {
          const Icon = x.icon;
          const active = tab === x.id;
          return (
            <button
              key={x.id}
              type="button"
              onClick={() => { setPreset(null); setTab(x.id); }}
              className={[
                'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                active ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:bg-bg-subtle',
              ].join(' ')}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {x.label}
            </button>
          );
        })}
      </nav>

      {tab === 'overview' && <Overview profile={profile} earnings={earnings} money={money} onSell={() => { setPreset(null); setTab('sell'); }} onCollect={() => { setPreset(null); setTab('collect'); }} />}
      {tab === 'customers' && <Customers onSell={(partyId) => goTo({ tab: 'sell', partyId })} onCollect={(partyId) => goTo({ tab: 'collect', partyId })} />}
      {tab === 'sell' && <SellPanel baseCurrency={baseCurrency} presetPartyId={preset?.tab === 'sell' ? preset.partyId : undefined} onDone={() => { void refresh(); }} />}
      {tab === 'collect' && <CollectPanel baseCurrency={baseCurrency} presetPartyId={preset?.tab === 'collect' ? preset.partyId : undefined} onDone={() => { void refresh(); }} />}
      {tab === 'activity' && <Activity orders={orders} collections={collections} money={money} onDelivered={() => { void refresh(); }} />}
    </div>
  );
}

function Overview({ profile, earnings, money, onSell, onCollect }: {
  profile: MandobProfile; earnings: MandobEarnings | null;
  money: (a: string, c?: string) => string; onSell: () => void; onCollect: () => void;
}): JSX.Element {
  const { t } = useTranslation('mandob');
  const hasCommission = profile.payScheme === 'salary_commission';
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard tone="brand" icon={Banknote} label={t('field.salary', { defaultValue: 'Monthly salary' })} value={money(profile.salary)} loading={!earnings} />
        <StatCard tone="success" icon={Coins} label={t('field.unpaidCommission', { defaultValue: 'Unpaid commission' })}
          value={money(earnings?.commissionAccrued ?? '0')} loading={!earnings}
          hint={hasCommission ? t('field.rateHint', { defaultValue: '{{rate}}% of collections', rate: profile.commissionRate }) : t('field.salaryOnly', { defaultValue: 'Salary only' })} />
        <StatCard tone="info" icon={HandCoins} label={t('field.lifetimeCommission', { defaultValue: 'Lifetime commission' })} value={money(earnings?.commissionLifetime ?? '0')} loading={!earnings} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onSell}><ShoppingBag className="me-2 h-4 w-4" />{t('field.newOrder', { defaultValue: 'New order' })}</Button>
        <Button variant="secondary" onClick={onCollect}><HandCoins className="me-2 h-4 w-4" />{t('field.collectPayment', { defaultValue: 'Collect payment' })}</Button>
      </div>
      {earnings && earnings.recent.length > 0 && (
        <Card>
          <h2 className="mb-2 text-sm font-semibold text-fg">{t('field.recentCommission', { defaultValue: 'Recent commission' })}</h2>
          <ul className="divide-y divide-border text-sm">
            {earnings.recent.slice(0, 8).map((e) => (
              <li key={e.id} className="flex items-center justify-between py-2">
                <span className="text-fg-muted">{t(`field.commType.${e.type}`, { defaultValue: e.note ?? e.type })}</span>
                <span className={`font-mono ${Number(e.amount) < 0 ? 'text-danger' : 'text-fg'}`}>{money(e.amount)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Customers({ onSell, onCollect }: {
  onSell: (partyId: string) => void; onCollect: (partyId: string) => void;
}): JSX.Element {
  const { t } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const [rows, setRows] = useState<MandobCustomer[] | null>(null);
  const [all, setAll] = useState(false);
  const [adding, setAdding] = useState(false);

  const reload = async (showAll: boolean): Promise<void> => {
    setRows(null);
    try { setRows(await listMyCustomers({ all: showAll })); } catch { toast.error(tc('errors:server')); }
  };
  useEffect(() => { void reload(all); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [all]);

  const columns = [
    {
      id: 'name', header: t('field.customer', { defaultValue: 'Customer' }),
      cell: (r: MandobCustomer) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-fg">{r.displayName}</span>
            {r.isAssigned && <Badge tone="info">{t('field.assigned', { defaultValue: 'Mine' })}</Badge>}
          </div>
          <div className="truncate text-xs text-fg-muted">{r.code}{r.phone ? ` · ${r.phone}` : ''}</div>
        </div>
      ),
      sortValue: (r: MandobCustomer) => r.displayName,
      searchValue: (r: MandobCustomer) => `${r.displayName} ${r.code} ${r.phone ?? ''} ${r.email ?? ''}`,
    },
    {
      id: 'actions', header: '', align: 'end' as const,
      cell: (r: MandobCustomer) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onSell(r.id)}>{t('field.sell', { defaultValue: 'New order' })}</Button>
          <Button variant="secondary" size="sm" onClick={() => onCollect(r.id)}>{t('field.collect', { defaultValue: 'Collect' })}</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {adding && (
        <NewCustomerForm
          onCancel={() => setAdding(false)}
          onSaved={() => { setAdding(false); void reload(all); }}
        />
      )}
      <Card className="p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
        <span className="text-sm text-fg-muted">{t('field.customersHint', { defaultValue: 'Your assigned customers — switch to the full book any time.' })}</span>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-fg">
            <input type="checkbox" className="h-4 w-4 accent-brand" checked={all} onChange={(e) => setAll(e.target.checked)} />
            {t('field.showAll', { defaultValue: 'All customers' })}
          </label>
          {!adding && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <UserPlus className="me-2 h-4 w-4" />{t('field.newCustomer', { defaultValue: 'New customer' })}
            </Button>
          )}
        </div>
      </div>
      {!rows ? (
        <div className="p-4"><Skeleton className="h-40 w-full" /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={all ? t('field.noCustomers', { defaultValue: 'No customers' }) : t('field.noAssigned', { defaultValue: 'No customers assigned to you' })}
          description={!all ? t('field.noAssignedHint', { defaultValue: 'Tick “All customers” to sell or collect from anyone.' }) : undefined}
        />
      ) : (
        <DataTable columns={columns} data={rows} rowKey={(r) => r.id} searchPlaceholder={t('field.searchCustomers', { defaultValue: 'Search customers…' })} />
      )}
      </Card>
    </div>
  );
}

function NewCustomerForm({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }): JSX.Element {
  const { t } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [coords, setCoords] = useState<LatLng | null>(null);
  const [busy, setBusy] = useState(false);

  const valid = displayName.trim().length > 0;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await createMyCustomer({
        displayName: displayName.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        latitude: coords?.lat,
        longitude: coords?.lng,
      });
      toast.success(t('field.customerAdded', { defaultValue: 'Customer added' }));
      onSaved();
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
        <UserPlus className="h-5 w-5 text-brand" aria-hidden />{t('field.newCustomer', { defaultValue: 'New customer' })}
      </h2>
      <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={submit} noValidate>
        <FormField id="nc-name" label={t('field.custName', { defaultValue: 'Name' })} required>
          <Input id="nc-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required autoFocus />
        </FormField>
        <FormField id="nc-phone" label={t('field.custPhone', { defaultValue: 'Phone' })}>
          <PhoneInput id="nc-phone" value={phone} onChange={setPhone} />
        </FormField>
        <FormField id="nc-email" label={t('field.custEmail', { defaultValue: 'Email' })}>
          <Input id="nc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <div className="sm:col-span-2 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-fg">{t('field.location', { defaultValue: 'Location' })}</label>
            {coords ? (
              <span className="font-mono text-xs text-fg-muted">
                {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                <button type="button" onClick={() => setCoords(null)} className="ms-2 text-danger hover:underline">
                  {tc('actions.remove', { defaultValue: 'Remove' })}
                </button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
                <MapPin className="h-3.5 w-3.5" aria-hidden />{t('field.locationHint', { defaultValue: 'Search, click, or drag the pin' })}
              </span>
            )}
          </div>
          <MapPicker
            value={coords}
            onChange={setCoords}
            searchPlaceholder={t('field.locationSearch', { defaultValue: 'Search an address or place…' })}
            unavailableLabel={t('field.mapUnavailable', { defaultValue: 'Map unavailable (offline).' })}
          />
        </div>
        <div className="sm:col-span-2 flex gap-2">
          <Button type="submit" loading={busy} disabled={!valid}>{t('field.addCustomer', { defaultValue: 'Add customer' })}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>{tc('actions.cancel', { defaultValue: 'Cancel' })}</Button>
        </div>
      </form>
    </Card>
  );
}

function SellPanel({ baseCurrency, presetPartyId, onDone }: {
  baseCurrency: string; presetPartyId?: string; onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const { i18n } = useTranslation();
  const [customers, setCustomers] = useState<MandobCustomer[]>([]);
  const [catalog, setCatalog] = useState<MandobCatalogItem[]>([]);
  const [partyId, setPartyId] = useState<string | undefined>(presetPartyId);
  const [currency, setCurrency] = useState(baseCurrency);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<SellLine[]>([{ description: '', quantity: '1', unitPrice: '0' }]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ id: string; docNumber: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [cs, items] = await Promise.all([listMyCustomers({ all: true }), listMandobCatalog()]);
        setCustomers(cs); setCatalog(items);
      } catch { toast.error(tc('errors:server')); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (presetPartyId) setPartyId(presetPartyId); }, [presetPartyId]);

  const total = useMemo(
    () => lines.reduce((sum, l) => sum + Number(l.quantity || 0) * Number(l.unitPrice || 0), 0),
    [lines],
  );
  const setLine = (i: number, p: Partial<SellLine>): void => setLines((s) => s.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
  const addLine = (): void => setLines((s) => [...s, { description: '', quantity: '1', unitPrice: '0' }]);
  const removeLine = (i: number): void => setLines((s) => (s.length > 1 ? s.filter((_, idx) => idx !== i) : s));
  const pickItem = (i: number, itemId: string | undefined): void => {
    const item = catalog.find((c) => c.id === itemId);
    // Picking a (new) item resets to its base unit.
    setLine(i, {
      itemId,
      itemUnitId: undefined,
      description: item ? item.name : '',
      unitPrice: item?.salePrice ?? '0',
    });
    if (item?.currencyCode) setCurrency(item.currencyCode);
  };
  // Pick a packaging unit (or base) for a line — sets the unit price to that
  // unit's price. A unit with no price of its own falls back to the item's base
  // price TIMES ITS FACTOR: a carton of 12 is twelve pieces leaving the shelf,
  // so it cannot be sold for the price of one.
  const pickUnit = (i: number, item: MandobCatalogItem, unitId: string): void => {
    const u = item.units.find((x) => x.id === unitId);
    const fallback = item.salePrice != null && u
      ? new Decimal(item.salePrice).times(u.factor).toFixed(4)
      : item.salePrice;
    setLine(i, {
      itemUnitId: unitId || undefined,
      unitPrice: (u ? u.salePrice ?? fallback : item.salePrice) ?? '0',
    });
  };

  const valid = Boolean(partyId) && lines.every((l) => (l.description.trim() || l.itemId) && Number(l.quantity) > 0);

  const submit = async (): Promise<void> => {
    if (!partyId) return;
    setBusy(true);
    try {
      const r = await mandobSell({
        partyId, currencyCode: currency, notes: notes.trim() || undefined,
        lines: lines.map((l) => ({
          ...(l.itemId ? { itemId: l.itemId } : {}),
          ...(l.itemId && l.itemUnitId ? { itemUnitId: l.itemUnitId } : {}),
          description: l.description.trim() || undefined,
          quantity: l.quantity || '1',
          unitPrice: l.unitPrice || '0',
        })),
      });
      setResult(r);
      toast.success(t('field.orderCreated', { defaultValue: 'Order {{n}} created', n: r.docNumber }));
      setLines([{ description: '', quantity: '1', unitPrice: '0' }]); setNotes('');
      onDone();
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  const custOptions = customers.map((c) => ({ value: c.id, label: c.displayName, sublabel: c.code, keywords: c.phone ?? '' }));
  const itemOptions = catalog.map((c) => ({ value: c.id, label: c.name, sublabel: c.code }));

  return (
    <Card className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField id="sell-cust" label={t('field.customer', { defaultValue: 'Customer' })} required>
          <Combobox options={custOptions} value={partyId} onChange={(v) => setPartyId(v)} placeholder={t('field.pickCustomer', { defaultValue: 'Pick a customer…' })} />
        </FormField>
        <FormField id="sell-cur" label={tc('columns.currency', { defaultValue: 'Currency' })}>
          <CurrencySelect id="sell-cur" value={currency} onChange={setCurrency} />
        </FormField>
      </div>

      <div className="flex flex-col gap-2">
        {lines.map((l, i) => {
          const lineItem = l.itemId ? catalog.find((c) => c.id === l.itemId) : undefined;
          const lineUnits = lineItem?.units ?? [];
          return (
          <div key={i} className="grid grid-cols-1 gap-2 rounded-lg border border-border p-2 sm:grid-cols-12 sm:items-end">
            <div className="sm:col-span-4">
              <Combobox options={itemOptions} value={l.itemId} onChange={(v) => pickItem(i, v)} placeholder={t('field.pickItem', { defaultValue: 'Item (or type below)' })} />
              <Input className="mt-2" value={l.description} onChange={(e) => setLine(i, { description: e.target.value, itemId: undefined, itemUnitId: undefined })} placeholder={t('field.lineDesc', { defaultValue: 'Description' })} />
            </div>
            <div className="sm:col-span-2">
              {lineUnits.length > 0 ? (
                <select
                  className="h-10 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg"
                  value={l.itemUnitId ?? ''}
                  onChange={(e) => lineItem && pickUnit(i, lineItem, e.target.value)}
                  aria-label={t('field.unit', { defaultValue: 'Unit' })}
                >
                  <option value="">{t('field.baseUnit', { defaultValue: 'Single' })}</option>
                  {lineUnits.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} (×{u.factor})</option>
                  ))}
                </select>
              ) : (
                <span className="text-xs text-fg-muted">{lineItem ? t('field.baseUnit', { defaultValue: 'Single' }) : '—'}</span>
              )}
            </div>
            <div className="sm:col-span-2">
              <Input inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} placeholder={t('field.qty', { defaultValue: 'Qty' })} aria-label={t('field.qty', { defaultValue: 'Qty' })} />
            </div>
            <div className="sm:col-span-2">
              <Input inputMode="decimal" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} placeholder={t('field.price', { defaultValue: 'Unit price' })} aria-label={t('field.price', { defaultValue: 'Unit price' })} />
            </div>
            <div className="flex justify-end sm:col-span-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(i)} disabled={lines.length === 1}>{tc('actions.remove', { defaultValue: 'Remove' })}</Button>
            </div>
          </div>
          );
        })}
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={addLine}>{t('field.addLine', { defaultValue: 'Add line' })}</Button>
        </div>
      </div>

      <FormField id="sell-notes" label={tc('columns.notes', { defaultValue: 'Notes' })}>
        <Input id="sell-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </FormField>

      <div className="flex items-center justify-between border-t border-border pt-3">
        <div className="text-sm text-fg-muted">
          {t('field.subtotal', { defaultValue: 'Subtotal' })}:{' '}
          <span className="font-mono text-fg">{formatMoney(total.toFixed(2), { locale: i18n.language, currency })}</span>
          <span className="ms-1 text-xs">{t('field.taxNote', { defaultValue: '(tax added on save)' })}</span>
        </div>
        <Button onClick={submit} loading={busy} disabled={!valid}>{t('field.createOrder', { defaultValue: 'Create order' })}</Button>
      </div>

      {result && (
        <div className="rounded-lg border border-success/40 bg-success/10 p-3 text-sm">
          {t('field.orderCreated', { defaultValue: 'Order {{n}} created', n: result.docNumber })} — {t('field.deliverHint', { defaultValue: 'deliver it from Activity to make an invoice.' })}
        </div>
      )}
    </Card>
  );
}

function CollectPanel({ baseCurrency, presetPartyId, onDone }: {
  baseCurrency: string; presetPartyId?: string; onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const [customers, setCustomers] = useState<MandobCustomer[]>([]);
  const [partyId, setPartyId] = useState<string | undefined>(presetPartyId);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(baseCurrency);
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try { setCustomers(await listMyCustomers({ all: true })); } catch { toast.error(tc('errors:server')); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (presetPartyId) setPartyId(presetPartyId); }, [presetPartyId]);

  const valid = Boolean(partyId) && Number(amount) > 0 && /^[A-Z]{3}$/.test(currency);

  const submit = async (): Promise<void> => {
    if (!partyId) return;
    setBusy(true);
    try {
      const r = await mandobCollect({
        partyId, amount, currencyCode: currency,
        method: method.trim() || undefined, reference: reference.trim() || undefined,
      });
      toast.success(
        r.posted
          ? t('field.collected', { defaultValue: 'Collected — {{n}}', n: r.docNumber })
          : t('field.collectedUnposted', { defaultValue: 'Recorded {{n}} (office will post)', n: r.docNumber }),
      );
      setAmount(''); setReference('');
      onDone();
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  const custOptions = customers.map((c) => ({ value: c.id, label: c.displayName, sublabel: c.code, keywords: c.phone ?? '' }));

  return (
    <Card className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField id="col-cust" label={t('field.customer', { defaultValue: 'Customer' })} required>
          <Combobox options={custOptions} value={partyId} onChange={(v) => setPartyId(v)} placeholder={t('field.pickCustomer', { defaultValue: 'Pick a customer…' })} />
        </FormField>
        <FormField id="col-amt" label={t('field.amount', { defaultValue: 'Amount' })} required>
          <Input id="col-amt" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </FormField>
        <FormField id="col-cur" label={tc('columns.currency', { defaultValue: 'Currency' })}>
          <CurrencySelect id="col-cur" value={currency} onChange={setCurrency} />
        </FormField>
        <FormField id="col-method" label={t('field.method', { defaultValue: 'Method' })}>
          <Input id="col-method" value={method} onChange={(e) => setMethod(e.target.value)} />
        </FormField>
        <FormField id="col-ref" label={t('field.reference', { defaultValue: 'Reference' })}>
          <Input id="col-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
        </FormField>
      </div>
      <p className="text-xs text-fg-muted">{t('field.collectNote', { defaultValue: 'Collections drive your commission and post to the customer’s account.' })}</p>
      <div className="flex justify-end">
        <Button onClick={submit} loading={busy} disabled={!valid}><HandCoins className="me-2 h-4 w-4" />{t('field.recordCollection', { defaultValue: 'Record collection' })}</Button>
      </div>
    </Card>
  );
}

function Activity({ orders, collections, money, onDelivered }: {
  orders: MandobOrderRow[]; collections: MandobCollectionRow[];
  money: (a: string, c?: string) => string; onDelivered: () => void;
}): JSX.Element {
  const { t } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const [delivering, setDelivering] = useState<string | null>(null);

  const deliver = async (id: string): Promise<void> => {
    setDelivering(id);
    try {
      const r = await mandobDeliver(id);
      toast.success(t('field.delivered', { defaultValue: 'Delivered — invoice {{n}}', n: r.docNumber }));
      onDelivered();
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      toast.error(code === 'already_delivered' ? t('field.alreadyDelivered', { defaultValue: 'Already delivered.' }) : tc('errors:server'));
    } finally { setDelivering(null); }
  };

  const orderCols = [
    { id: 'doc', header: tc('columns.number', { defaultValue: 'No.' }), cell: (r: MandobOrderRow) => <span className="font-mono text-sm">{r.docNumber}</span>, searchValue: (r: MandobOrderRow) => `${r.docNumber} ${r.partyName}` },
    { id: 'party', header: t('field.customer', { defaultValue: 'Customer' }), cell: (r: MandobOrderRow) => r.partyName },
    { id: 'date', header: tc('columns.date', { defaultValue: 'Date' }), cell: (r: MandobOrderRow) => r.docDate },
    { id: 'total', header: tc('columns.total', { defaultValue: 'Total' }), align: 'end' as const, cell: (r: MandobOrderRow) => <span className="font-mono">{money(r.total, r.currencyCode)}</span>, sortValue: (r: MandobOrderRow) => Number(r.total) },
    { id: 'status', header: tc('columns.status', { defaultValue: 'Status' }), cell: (r: MandobOrderRow) => <Badge tone={r.status === 'fulfilled' ? 'success' : r.status === 'draft' ? 'neutral' : 'info'}>{r.status}</Badge> },
    {
      id: 'actions', header: '', align: 'end' as const,
      cell: (r: MandobOrderRow) => (r.status === 'fulfilled'
        ? <span className="inline-flex items-center gap-1 text-xs text-success"><PackageCheck className="h-4 w-4" />{t('field.deliveredShort', { defaultValue: 'Delivered' })}</span>
        : <Button variant="secondary" size="sm" loading={delivering === r.id} onClick={() => deliver(r.id)}><Truck className="me-2 h-4 w-4" />{t('field.deliver', { defaultValue: 'Deliver' })}</Button>),
    },
  ];
  const colCols = [
    { id: 'doc', header: tc('columns.number', { defaultValue: 'No.' }), cell: (r: MandobCollectionRow) => <span className="font-mono text-sm">{r.docNumber}</span>, searchValue: (r: MandobCollectionRow) => `${r.docNumber} ${r.partyName}` },
    { id: 'party', header: t('field.customer', { defaultValue: 'Customer' }), cell: (r: MandobCollectionRow) => r.partyName },
    { id: 'date', header: tc('columns.date', { defaultValue: 'Date' }), cell: (r: MandobCollectionRow) => r.paymentDate },
    { id: 'amount', header: t('field.amount', { defaultValue: 'Amount' }), align: 'end' as const, cell: (r: MandobCollectionRow) => <span className="font-mono">{money(r.amount, r.currencyCode)}</span>, sortValue: (r: MandobCollectionRow) => Number(r.amount) },
  ];

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-fg">{t('field.myOrders', { defaultValue: 'My orders' })}</h2>
        {orders.length === 0 ? (
          <EmptyState title={t('field.noOrders', { defaultValue: 'No orders yet' })} />
        ) : (
          <Card className="p-0"><DataTable columns={orderCols} data={orders} rowKey={(r) => r.id} searchPlaceholder={t('field.searchOrders', { defaultValue: 'Search orders…' })} /></Card>
        )}
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-fg">{t('field.myCollections', { defaultValue: 'My collections' })}</h2>
        {collections.length === 0 ? (
          <EmptyState title={t('field.noCollections', { defaultValue: 'No collections yet' })} />
        ) : (
          <Card className="p-0"><DataTable columns={colCols} data={collections} rowKey={(r) => r.id} searchPlaceholder={t('field.searchCollections', { defaultValue: 'Search collections…' })} /></Card>
        )}
      </section>
    </div>
  );
}
